import { useMemo, useState } from "react";
import { Outlet, useParams } from "react-router";
import { useQueryClient, useIsMutating } from "@tanstack/react-query";
import BreadcrumbNavigation from "~/components/Breadcrumbs";
import Header from "~/components/Header";
import Toolbar from "~/components/Toolbar";
import AutoSaveStatus from "~/components/AutoSaveStatus";
import { BreadcrumbItem } from "~/types/global";
import { ReportEditControls } from "~/components/ReportEditControls";
import { REPORT_DETAIL_STORE_INSTANCE } from "~/store/details";
import { useReportActionsWithAutoSave } from "~/hooks/useReportActionsWithAutoSave";
import { useBackButtonGuard } from "~/hooks/useBackButtonGuard";
import { useFolderCRUD } from "~/hooks/useFolderCRUD";
import { FavoriteButton } from "~/components/FavoriteButton";

/**
 * Recursively finds a folder in the decrypted hierarchy forest and returns its
 * favorite state. Returns undefined when the folder isn't present.
 */
function findFolderFavorited(forest: any[] | undefined, folderId: string): boolean | undefined {
  if (!Array.isArray(forest)) return undefined;
  for (const folder of forest) {
    if (folder?.folder_id === folderId) return folder.is_favorited === true;
    const nested = findFolderFavorited(folder?.data, folderId);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

const DefaultLayout = () => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const { folderId, id: reportId } = useParams<{ folderId?: string; id?: string; type?: string }>();
  const queryClient = useQueryClient();
  // Track favorite mutations so the cache-derived base re-evaluates after toggles.
  const favoriteMutationCount = useIsMutating({ mutationKey: ["toggleFavorite"] });
  const {
    selectors: {
      useIsPendingSave,
      useHasPendingChanges,
      useIsAutoSaveEnabled,
      useRefetchReport,
    },
    events: { clearPendingChanges },
  } = REPORT_DETAIL_STORE_INSTANCE;
  const refetchReport = useRefetchReport();
  const actions = useReportActionsWithAutoSave(refetchReport || undefined);
  const isSaving = useIsPendingSave();
  const hasPendingChanges = useHasPendingChanges();
  const isAutoSaveEnabled = useIsAutoSaveEnabled();
  const showEditControls = hasPendingChanges && !isAutoSaveEnabled;

  // Derive the favorite base for the current folder/report from the list cache
  // (the FavoriteButton layers the optimistic store override on top of this).
  // Returns undefined when the resource isn't present in any list cache.
  const deriveFavoriteBase = (matcher: (item: any) => boolean): boolean | undefined => {
    const allListCaches = queryClient.getQueriesData<any>({ queryKey: ["reports", "list"] });
    for (const [, cacheData] of allListCaches) {
      if (!cacheData) continue;
      const items = cacheData.data ?? cacheData.items;
      if (!Array.isArray(items)) continue;
      const found = items.find(matcher);
      if (found) return found.is_favorited === true;
    }
    return undefined;
  };

  const folderFavInCache = useMemo(
    () => (folderId ? deriveFavoriteBase((item) => item.folder_id === folderId) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, folderId, favoriteMutationCount]
  );

  // On a hard refresh directly into a folder route, the folder's own favorite
  // state isn't in any list cache (the list holds the folder's CHILDREN; the
  // parent list isn't loaded). Fall back to the folder hierarchy, which carries
  // is_favorited per folder. Only fetched when the cache lookup misses.
  const { useHierarchicalFoldersQuery } = useFolderCRUD();
  const needFolderFavFromHierarchy = !!folderId && folderFavInCache === undefined;
  const { data: hierarchicalFolders } = useHierarchicalFoldersQuery(undefined, {
    enabled: needFolderFavFromHierarchy,
    staleTime: 60000,
  });

  const isFolderFavorited = useMemo(() => {
    if (!folderId) return false;
    if (folderFavInCache !== undefined) return folderFavInCache;
    return findFolderFavorited(hierarchicalFolders as any[] | undefined, folderId) ?? false;
  }, [folderId, folderFavInCache, hierarchicalFolders]);

  const isReportFavorited = useMemo(
    () =>
      reportId && !folderId
        ? (deriveFavoriteBase((item) => item.user_reports_id === reportId || item.id === reportId) ?? false)
        : false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, reportId, folderId, favoriteMutationCount]
  );

  useBackButtonGuard({
    hasUnsavedChanges: hasPendingChanges,
    onConfirmNavigation: clearPendingChanges,
  });

  return (
    <div className="h-screen flex flex-col">
      <Header />
      <div className="px-8 gap-6 flex items-center justify-between flex-wrap min-h-11 md:min-h-9">
        <div className="flex-1 flex items-center gap-2">
          <BreadcrumbNavigation breadcrumbs={breadcrumbs} />
          {folderId && (
            <FavoriteButton
              size={16}
              resourceType="folder"
              resourceId={folderId}
              isFavorited={isFolderFavorited}
              className=""
            />
          )}
          {reportId && !folderId && (
            <FavoriteButton
              resourceType="report"
              resourceId={reportId}
              isFavorited={isReportFavorited}
            />
          )}
        </div>
        {reportId && !folderId && (
          <div className="flex gap-8 items-center">
            <ReportEditControls
              show={showEditControls}
              onSave={actions.forceSave}
              onCancel={actions.cancelChanges}
              isSaving={isSaving}
            />
            <AutoSaveStatus />
          </div>
        )}
      </div>
      <main className="flex-1 flex flex-col min-h-0">
        <div className="w-full px-8 pt-4 pb-2">
          <Toolbar />
        </div>
        <div className="flex-1 w-full">
          <Outlet context={{ setBreadcrumbs }} />
        </div>
      </main>
    </div>
  );
};

export default DefaultLayout;
